import { FC, HTMLAttributes, useEffect, useState } from "react";
import { useStyles } from "./SetProfile.style";
import LogoIcon from "../../assets/icons/logo_onmo+_3x.webp";
import { StaticImage } from "../../components/core/StaticImage/StaticImage";
import SubscriptionTv from "../../assets/subscription/subscription-tv.webp";
import SubscriptionTv2x from "../../assets/subscription/subscription-tv@2x.webp";
import SubscriptionTv3x from "../../assets/subscription/subscription-tv@3x.webp";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import { Typography } from "../../components/core/Typography/Typography";
import { TextField } from "../../components/core/TextField/TextField";
import {
  useApplyAvatar,
  useFetchFreeAvatars,
} from "../../hooks/useSelectAvatarQuery";
import Button from "../../components/core/Button/Button";
import { isValidUsernameFormat } from "../../models/regex";
import { Icon } from "../../components/core/Icon/Icon";
import clsx from "classnames";
import {
  Inventory,
  InventoryAvatarData,
  meAAAGQL,
  Query,
  UpdateProfileInput,
  updateProfileGQL,
  updateUserMetadataGQL,
} from "@onmo/graphql/graphql";
import { useMutation, useQuery } from "@apollo/client";
import { AnalyticsGA4 } from "../../models/analyticsGA4";
import { useGamepadContext } from "../../contexts/GamepadProvider";
import { useTranslation } from "react-i18next";
import { useCurrentProfile } from "../../hooks/useProfileSession";

const SetProfile: FC<HTMLAttributes<HTMLDivElement>> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { data: dataMe, refetch } = useQuery<{ me: Query["me"] }>(meAAAGQL);
  const [username, setUsername] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<Inventory>();
  const [usernameError, setUsernameError] = useState("");
  const [updateProfile] = useMutation<
    { updateProfile: { message: string } },
    { input: UpdateProfileInput }
  >(updateProfileGQL);
  const [updateUserMetadata] = useMutation(updateUserMetadataGQL);

  const { freeAvatars: avatars } = useFetchFreeAvatars();
  const { currentProfile: profile } = useCurrentProfile();
  const applyAvatar = useApplyAvatar();
  const { setCallback, cleanup } = useGamepadContext();

  const validateUsername = (username: string): string => {
    if (username.length < 3 || username.length > 16) {
      return t("SetProfile_Username must be between 3-16 characters long.");
    }

    if (!isValidUsernameFormat(username)) {
      return t("SetProfile_Username only allows letters, numbers, _ and -.");
    }

    return "";
  };

  const onSubmit = async () => {
    const trimmedUsername = username.trim();
    const error = validateUsername(trimmedUsername);
    if (error) {
      setUsernameError(error);
      return;
    }

    if (isSubmitting) return;
    try {
      setIsSubmitting(true);

      if (!selectedAvatar) return;
      AnalyticsGA4.profileSetup(selectedAvatar.title);

      const currentOnboarding = JSON.parse(
        dataMe?.me?.metadata?.onboarding || "{}",
      );
      const updatedOnboarding = {
        ...currentOnboarding,
        hasCheckedUserNameAvatar: true,
      };

      // Sequential: BE races creating the main profile on parallel calls
      if (profile) {
        await updateProfile({
          variables: {
            input: {
              id: profile.id,
              action: { editDetails: { username: trimmedUsername } },
            },
          },
          // updateProfile returns only a message
          update: (cache) => {
            cache.modify({
              id: cache.identify({ __typename: "Profile", id: profile.id }),
              fields: { username: () => trimmedUsername },
            });
          },
        });
        await applyAvatar(selectedAvatar, profile.id);
      }
      await updateUserMetadata({
        variables: {
          input: { onboarding: JSON.stringify(updatedOnboarding) },
        },
        update: (cache) => {
          const existingMeData = cache.readQuery<{ me: Query["me"] }>({
            query: meAAAGQL,
          });
          if (existingMeData?.me) {
            cache.writeQuery({
              query: meAAAGQL,
              data: {
                me: {
                  ...existingMeData.me,
                  metadata: {
                    ...existingMeData.me.metadata,
                    onboarding: JSON.stringify(updatedOnboarding),
                  },
                },
              },
            });
          }
        },
      });
      await refetch();
    } catch (error) {
      console.error("Failed:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const initialUsername = profile?.name || dataMe?.me.username;
  useEffect(() => {
    if (initialUsername) setUsername(initialUsername);
  }, [initialUsername]);

  useEffect(() => {
    if (avatars.length > 0 && !selectedAvatar) {
      setSelectedAvatar(avatars?.[0] as unknown as Inventory);
    }
  }, [avatars, selectedAvatar]);

  useEffect(() => {
    setCallback({
      onBackAction: async () => {
        await onSubmit();
      },
    });
    return () => cleanup();
  }, [setCallback, cleanup, username, selectedAvatar]);

  return (
    <div className={classes.root}>
      <StaticImage
        src={SubscriptionTv}
        src2x={SubscriptionTv2x}
        src3x={SubscriptionTv3x}
        className={classes.backgroundImage}
      />
      <div className={classes.header}>
        <StaticImage src={LogoIcon} alt="ONMO+" className={classes.logo} />
      </div>
      <div className={classes.content}>
        <div className={classes.titleContent}>
          <Typography variant={TypographyVariant.BodyB1Bold}>
            {t("SetProfile_Create Your Account")}
          </Typography>
          <Typography
            variant={TypographyVariant.BodyB5Medium}
            color={TypographyColor.Silver}
          >
            {t("SetProfile_Pick a username and an avatar to get started!")}
          </Typography>
        </div>
        <div className={classes.usernameInputContainer}>
          <TextField
            fullWidth
            inputProps={{
              placeholder: t("SetProfile_Enter username"),
              value: username,
              dataGamepad: "input-username",
            }}
            isShowClear
            onChangeValue={(value) => {
              setUsername(value);
              if (usernameError) setUsernameError("");
            }}
            isError={!!usernameError}
          />
          {usernameError && (
            <Typography
              variant={TypographyVariant.BodyB6Regular}
              color={TypographyColor.Error}
              className={classes.warning}
            >
              <Icon
                size={13}
                name="icon-bold-info-circle"
                className={classes.warningIcon}
              />
              {usernameError}
            </Typography>
          )}
        </div>
        <div className={classes.avatarContainer}>
          {avatars.map((avatar) => {
            const isActive = selectedAvatar?.id === avatar.id;
            return (
              <div
                className={clsx(classes.avatarWrapper, {
                  active: isActive,
                })}
                key={avatar.id}
                onClick={() =>
                  setSelectedAvatar(avatar as unknown as Inventory)
                }
                data-gamepad="avatar"
                data-selected-class={classes.active}
              >
                <StaticImage
                  alt="avatar"
                  className={classes.avatar}
                  src={(avatar.data as InventoryAvatarData)?.url}
                />
                {isActive && (
                  <Icon
                    name="icon-bold-tick-circle"
                    className={classes.checkIcon}
                    size={24}
                  />
                )}
              </div>
            );
          })}
        </div>
        <Button
          fullWidth
          size="large"
          disabled={!username || !selectedAvatar || isSubmitting}
          loading={isSubmitting}
          onClick={onSubmit}
        >
          {t("SetProfile_Continue")}
        </Button>
      </div>
    </div>
  );
};

export default SetProfile;
